import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Sparkles } from 'lucide-react';
import { api } from '../services';
import type { Avatar } from '../services/types';
import { useAuth } from '../contexts/AuthContext';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { haptic } from '../utils/haptic';
import { Sixy } from './Sixy';

/**
 * Onboarding obrigatório de avatar.
 *
 * Mostra um overlay full-screen quando:
 *  - usuário está autenticado E
 *  - profile.role === 'student' (admins/companies pulam) E
 *  - profile.avatarId é vazio (usuário novo ou nunca escolheu)
 *
 * Comportamento:
 *  - Bloqueia o app até escolher (sem botão "depois")
 *  - Lista os avatares "free" + os "minPoints/minRank" que o usuário
 *    já desbloqueou. Locked vão pra Profile depois.
 *  - Confete + haptic ao escolher
 *
 * Pra testers atuais que JÁ TÊM avatarId, esse componente é completamente
 * invisível — não causa fricção retroativa.
 */
export function AvatarOnboarding() {
  const { profile, updateUserProfile } = useAuth();
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Trigger: profile carregado, é student, e não tem avatar.
  const shouldShow = Boolean(
    profile && profile.role === 'student' && !profile.avatarId,
  );

  useEffect(() => {
    if (!shouldShow) return;
    let cancelled = false;
    (async () => {
      try {
        // Usa api.getAvatars() em vez de query supabase direto porque o
        // helper faz o mapping snake_case → camelCase (image_url → imageUrl).
        // Sem isso, todos os avatars caíam no placeholder de estrelas porque
        // `avatar.imageUrl` ficava undefined.
        const data = await api.getAvatars(false);
        if (cancelled) return;
        // Mostra TODOS os avatares ativos no onboarding inicial. Filtros por
        // unlockType (mission/minPoints) só fazem sentido pra escolha DEPOIS
        // no Profile, quando o user já tem contexto. Aqui o objetivo é que
        // ele consiga escolher QUALQUER COISA pra entrar — preso na tela é
        // muito pior que dar acesso a um avatar locked-em-teoria.
        setAvatars(data);
      } catch (err) {
        console.error('[AvatarOnboarding] erro listando avatares', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shouldShow]);

  // Escape hatch: se a lista veio vazia (RLS bloqueando, banco sem avatares
  // cadastrados, query falhou), permite continuar com o avatar 'default'.
  // Sem isso, o user fica preso na tela permanentemente.
  const handleSkip = async () => {
    haptic('light');
    setSaving(true);
    try {
      await updateUserProfile({ avatarId: 'default' });
      // No happy path, profile.avatarId muda, shouldShow vira false e o
      // componente desmonta. Não precisa setSaving(false). Mas se shouldShow
      // não desmontar (ex.: re-monte por outra causa), o finally garante
      // que o botão não fique preso em "Salvando…".
    } catch (err) {
      console.error('[AvatarOnboarding] falha no skip', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePick = (id: string) => {
    haptic('light');
    setPicked(id);
  };

  const handleConfirm = async () => {
    if (!picked || saving) return;
    setSaving(true);
    haptic('success');
    try {
      // Bônus de 20 Six Coins por escolher um avatar (vs pular).
      // Incentivo pra reduzir churn no skip sem forçar fricção.
      const bonus = 20;
      await updateUserProfile({
        avatarId: picked,
        points: (profile?.points || 0) + bonus,
      });
      // O effect que controla `shouldShow` reage automaticamente: profile.avatarId
      // muda, shouldShow vira false, overlay some.
    } catch (err) {
      console.error('[AvatarOnboarding] falha salvando avatar', err);
    } finally {
      // Defensivo: se shouldShow não desmontar imediatamente (ex.: profile
      // re-fetched mantém avatarId vazio por algum motivo), o botão volta
      // pro estado clicável em vez de ficar preso em "Salvando…".
      setSaving(false);
    }
  };

  /** Pula a escolha — seta avatar default sem bonus. User pode escolher
   *  depois pelo Profile, e aí ganha o bonus de 20 coins. */
  const handleSkipForNow = async () => {
    haptic('light');
    setSaving(true);
    try {
      await updateUserProfile({ avatarId: 'default' });
    } catch (err) {
      console.error('[AvatarOnboarding] falha no skip-for-now', err);
    } finally {
      setSaving(false);
    }
  };

  // Focus trap (WCAG 2.1.1) — overlay full-screen bloqueante exige que Tab
  // fique preso dentro. Sem isso, Tab vaza pro BottomNav atrás.
  const trapRef = useFocusTrap<HTMLDivElement>(shouldShow);

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          ref={trapRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          // z-index acima de tudo (banners/modais) mas abaixo de Toast (z-90)
          className="fixed inset-0 z-[80] flex flex-col bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 backdrop-blur-xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="avatar-onboarding-title"
        >
          {/* Header com Sixy — paddings compactos pra não roubar área da
              lista. Safe-area top respeitado mas com piso menor (12px vs 32px
              antes — overlay fullscreen não precisa de pt-8). */}
          <div
            className="px-6 pt-4 text-center shrink-0"
            style={{ paddingTop: 'max(env(safe-area-inset-top), 0.75rem)' }}
          >
            <div className="flex justify-center mb-2">
              <Sixy form="cloud" size="md" mood="happy" />
            </div>
            <h1
              id="avatar-onboarding-title"
              className="text-2xl font-black text-white mb-1"
            >
              Qual cara é a sua?
            </h1>
            <p className="text-sm text-slate-400 max-w-xs mx-auto leading-snug">
              Escolhe um — depois troca quando quiser, lá no perfil.
            </p>
          </div>

          {/* Grid de avatares. overscroll-contain previne bounce do iOS
              "rolar além do conteúdo" que dá sensação de scroll infinito. */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-3">
            {loading ? (
              <div className="grid grid-cols-3 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-2xl bg-white/5 animate-pulse"
                  />
                ))}
              </div>
            ) : avatars.length === 0 ? (
              <div className="text-center py-10 max-w-xs mx-auto">
                <p className="text-sm text-slate-300 mb-2 font-bold">
                  Ainda não tem avatares por aqui
                </p>
                <p className="text-xs text-slate-400 leading-snug mb-5">
                  A gente tá ajustando essa galeria. Bora seguir com o
                  avatar padrão — depois você troca lá no perfil.
                </p>
                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={saving}
                  className="press-feedback rounded-full bg-white text-slate-900 px-6 py-2.5 text-sm font-black uppercase tracking-wide disabled:opacity-50"
                >
                  {saving ? 'Salvando…' : 'Continuar com avatar padrão'}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {avatars.map((avatar) => {
                  const isPicked = picked === avatar.id;
                  return (
                    <motion.button
                      key={avatar.id}
                      type="button"
                      onClick={() => handlePick(avatar.id)}
                      whileTap={{ scale: 0.94 }}
                      className={`aspect-square rounded-2xl border-2 overflow-hidden relative transition-ui ${
                        isPicked
                          ? 'border-pink-400 shadow-[0_0_24px_rgba(244,114,182,0.5)] scale-[1.02]'
                          : 'border-white/10 hover:border-white/25 active:border-white/30'
                      }`}
                      aria-label={`Escolher avatar ${avatar.name || avatar.id}`}
                      aria-pressed={isPicked}
                    >
                      {avatar.imageUrl ? (
                        <img
                          src={avatar.imageUrl}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-full w-full bg-gradient-to-br from-pink-500/30 to-blue-500/30 flex items-center justify-center">
                          <Sparkles className="text-white/40" size={24} aria-hidden="true" />
                        </div>
                      )}
                      {isPicked && (
                        <div className="absolute inset-0 bg-pink-500/10 pointer-events-none" />
                      )}
                    </motion.button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer com CTA principal + escape hatch "Pular" */}
          <div
            className="px-5 pb-3 pt-2 shrink-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.75rem)' }}
          >
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!picked || saving}
              className={`w-full rounded-full py-3.5 text-sm font-black uppercase tracking-wide transition-ui ${
                picked && !saving
                  ? 'bg-gradient-to-r from-pink-500 to-blue-500 text-white shadow-lg shadow-pink-500/30 hover:brightness-110 active:scale-[0.98]'
                  : 'bg-white/5 text-slate-400 cursor-not-allowed'
              }`}
            >
              {saving
                ? 'Salvando…'
                : picked
                  ? 'Confirmar (+20 Six Coins)'
                  : 'Toca num pra começar'}
            </button>
            {/* Escape hatch: pular sem escolher. Não ganha bonus.
                Reduz churn no onboarding pra quem tem pressa. */}
            <button
              type="button"
              onClick={handleSkipForNow}
              disabled={saving}
              className="press-feedback mt-2 w-full py-2.5 text-xs font-bold text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50"
            >
              Pular por agora
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
